import { PLAY_STORE_URL } from "../lib/appLinks.js";
import { useState, useEffect } from "react";
import { ZH, taskFaq, taskSteps } from "../content/featuresContent.js";
import { EN } from "../content/featuresContent.en.js";
import { getLocale as i18nGetLocale } from "../lib/i18n.js";

// ─────────────────────────────────────────────────────────────────────────────
// Public /features page — the marketing tour for shop owners.
// Every screenshot under /features/*.jpg is a real capture of the demo shop
// (示範美容店) with phone numbers and emails redacted. Never swap in a real
// shop's customer data. The PAWSIN numbers are real and were agreed with the
// shop owner; keep them exact if they are ever refreshed.
// Plan badges must match PLANS in App.jsx (onlineBooking = 免費版每月 10 個, 基本版起不限次數,
// whatsapp = 專業版+, WhatsApp AI 自動回覆 = Chatwoot / WATI，另外付費).
// ─────────────────────────────────────────────────────────────────────────────

const IMG = (f) => "/features/" + f;

export const FEATURES_CSS = `
.pbf { --ink:#2E2A45; --soft:#6E6A86; --pink:#E0527C; --pink2:#F7A1B5; --line:#EEE8F2; --bg:#FFF8FA;
  font-family:'Nunito','PingFang HK','PingFang TC',-apple-system,BlinkMacSystemFont,sans-serif; color:var(--ink); background:#FCFCFF; min-height:100vh; }
.pbf * { box-sizing:border-box; }
.pbf a { color:inherit; }
.pbf-wrap { max-width:1120px; margin:0 auto; padding:0 16px; }
.pbf-nav { position:sticky; top:0; z-index:20; background:rgba(252,252,255,.92); backdrop-filter:blur(10px); border-bottom:1px solid var(--line); }
.pbf-nav .pbf-wrap { display:flex; align-items:center; justify-content:space-between; height:58px; gap:10px; }
.pbf-logo { font-weight:900; font-size:18px; color:var(--pink); text-decoration:none; white-space:nowrap; }
.pbf-navlinks { display:flex; gap:18px; font-size:14px; font-weight:800; color:var(--soft); }
.pbf-navlinks a { text-decoration:none; }
@media (max-width:760px){ .pbf-navlinks { display:none; } }
.pbf-btn { display:inline-flex; align-items:center; justify-content:center; gap:6px; border:none; cursor:pointer; text-decoration:none;
  font-weight:900; border-radius:99px; padding:13px 26px; font-size:16px; font-family:inherit; white-space:nowrap; }
.pbf-btn.pri { background:linear-gradient(135deg,var(--pink2),var(--pink)); color:#fff; box-shadow:0 10px 26px rgba(224,82,124,.32); }
.pbf-btn.sec { background:#fff; color:var(--pink); border:2px solid #F6C9D6; }
.pbf-btn.sm { padding:9px 18px; font-size:14px; }
.pbf-hero { background:linear-gradient(135deg,#FFE4EC 0%,#FFF5F8 50%,#F3EEFF 100%); padding:54px 0 60px; }
.pbf-hero .pbf-wrap { display:grid; grid-template-columns:1fr 1.15fr; gap:44px; align-items:center; }
@media (max-width:900px){ .pbf-hero .pbf-wrap { grid-template-columns:1fr; gap:28px; } .pbf-hero { padding:32px 0 40px; } }
.pbf-kick { font-size:14px; font-weight:900; color:var(--pink); letter-spacing:1px; margin:0 0 10px; }
.pbf-h1 { font-size:clamp(34px,5.4vw,56px); line-height:1.15; font-weight:900; margin:0 0 16px; letter-spacing:-1px; }
.pbf-lead { font-size:18px; line-height:1.65; color:var(--soft); font-weight:700; margin:0 0 24px; }
.pbf-ctas { display:flex; gap:10px; flex-wrap:wrap; }
.pbf-note { font-size:13px; color:var(--soft); font-weight:700; margin:14px 0 0; }
.pbf-play { display:inline-flex; align-items:center; gap:10px; margin-top:16px; padding:9px 18px 9px 14px; background:#111827; color:#fff; border-radius:12px; text-decoration:none; }
.pbf-play small { display:block; font-size:11px; font-weight:700; opacity:.8; line-height:1.2; }
.pbf-play b { display:block; font-size:16px; font-weight:900; line-height:1.2; }
.pbf-play:hover { background:#1F2937; }
.pbf-play, .pbf-play small, .pbf-play b { color:#fff !important; }
.pbf-video { width:100%; border-radius:20px; box-shadow:0 26px 60px rgba(224,82,124,.22); display:block; background:#fff; aspect-ratio:16/9; }
.pbf-sec { padding:64px 0; }
.pbf-sec.alt { background:var(--bg); }
.pbf-h2 { font-size:clamp(26px,3.6vw,38px); font-weight:900; margin:0 0 10px; letter-spacing:-.5px; text-align:center; }
.pbf-sub { font-size:17px; color:var(--soft); font-weight:700; text-align:center; margin:0 auto 38px; max-width:640px; line-height:1.6; }
.pbf-ba { display:grid; grid-template-columns:1fr 1fr; gap:16px; }
@media (max-width:760px){ .pbf-ba { grid-template-columns:1fr; } }
.pbf-ba > div { border-radius:22px; padding:24px; }
.pbf-ba h3 { margin:0 0 14px; font-size:19px; font-weight:900; }
.pbf-ba ul { margin:0; padding:0; list-style:none; }
.pbf-ba li { font-size:15.5px; font-weight:700; line-height:1.5; padding:8px 0; border-top:1px dashed rgba(0,0,0,.08); }
.pbf-ba li:first-child { border-top:none; }
.pbf-feat { display:grid; grid-template-columns:1fr 1.25fr; gap:40px; align-items:center; margin-bottom:64px; }
.pbf-feat:last-child { margin-bottom:0; }
.pbf-feat.flip > :first-child { order:2; }
@media (max-width:860px){ .pbf-feat, .pbf-feat.flip { grid-template-columns:1fr; gap:20px; } .pbf-feat.flip > :first-child { order:0; } }
.pbf-num { display:inline-flex; width:34px; height:34px; border-radius:50%; background:#FFE4EC; color:var(--pink); font-weight:900; align-items:center; justify-content:center; margin-bottom:12px; }
.pbf-feat h3 { font-size:clamp(22px,2.8vw,30px); font-weight:900; margin:0 0 12px; line-height:1.3; }
.pbf-feat p { font-size:16px; color:var(--soft); font-weight:700; line-height:1.7; margin:0 0 12px; }
.pbf-feat ul { list-style:none; margin:0 0 14px; padding-left:0; color:var(--ink); font-weight:700; font-size:15px; line-height:1.8; }
.pbf-feat li { position:relative; padding-left:24px; margin-bottom:4px; }
.pbf-feat li::before { content:'✓'; position:absolute; left:0; color:var(--pink); font-weight:900; }
.pbf-badge { display:inline-block; font-size:12px; font-weight:900; padding:3px 10px; border-radius:99px; background:#F3EEFF; color:#7C3AED; margin:2px 6px 2px 0; }
.pbf-badge.free { background:#E8F8EF; color:#16804A; }
.pbf-shot { width:100%; border-radius:16px; border:1px solid var(--line); box-shadow:0 18px 44px rgba(46,42,69,.12); display:block; background:#fff; }
.pbf-shots2 { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
.pbf-phone { border-radius:28px; border:8px solid #1d1d22; box-shadow:0 18px 40px rgba(46,42,69,.18); overflow:hidden; background:#fff; }
.pbf-phone img { width:100%; display:block; aspect-ratio:780/1500; object-fit:cover; object-position:top; }
.pbf-steps { display:grid; grid-auto-flow:column; grid-auto-columns:minmax(200px,230px); gap:18px; overflow-x:auto; padding:6px 4px 18px; scroll-snap-type:x mandatory; }
.pbf-step { scroll-snap-align:start; }
.pbf-step b { display:block; font-size:15px; font-weight:900; margin:12px 0 4px; }
.pbf-step span { display:block; font-size:13.5px; color:var(--soft); font-weight:700; line-height:1.5; }
.pbf-day { display:grid; grid-template-columns:repeat(auto-fit,minmax(250px,1fr)); gap:20px; }
.pbf-card { background:#fff; border:1px solid var(--line); border-radius:20px; overflow:hidden; box-shadow:0 8px 24px rgba(46,42,69,.06); }
.pbf-card img { width:100%; display:block; aspect-ratio:16/10; object-fit:cover; object-position:top; border-bottom:1px solid var(--line); }
.pbf-card div { padding:16px 18px 18px; }
.pbf-card small { font-size:12px; font-weight:900; color:var(--pink); letter-spacing:.5px; }
.pbf-card h4 { font-size:17px; font-weight:900; margin:4px 0 6px; }
.pbf-card p { font-size:14px; color:var(--soft); font-weight:700; line-height:1.6; margin:0; }
.pbf-case { background:linear-gradient(135deg,#2E2A45,#4A3F6B); color:#fff; border-radius:28px; padding:40px 32px; }
.pbf-case .pbf-kick { color:#F7A1B5; }
.pbf-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin:26px 0 18px; }
@media (max-width:760px){ .pbf-stats { grid-template-columns:1fr 1fr; } .pbf-case { padding:28px 20px; } }
.pbf-stat { background:rgba(255,255,255,.08); border-radius:18px; padding:18px; }
.pbf-stat b { display:block; font-size:clamp(30px,4vw,44px); font-weight:900; color:#F7A1B5; line-height:1.1; }
.pbf-stat span { font-size:14px; font-weight:700; opacity:.9; line-height:1.5; display:block; margin-top:6px; }
.pbf-plans { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; }
@media (max-width:900px){ .pbf-plans { grid-template-columns:1fr 1fr; } }
@media (max-width:480px){ .pbf-plans { grid-template-columns:1fr; } }
.pbf-plan { background:#fff; border:1px solid var(--line); border-radius:20px; padding:20px; }
.pbf-plan.hl { border:2px solid var(--pink); box-shadow:0 12px 30px rgba(224,82,124,.14); }
.pbf-plan h4 { margin:0 0 4px; font-size:17px; font-weight:900; }
.pbf-plan .pr { font-size:30px; font-weight:900; color:var(--pink); }
.pbf-plan .pr small { font-size:14px; color:var(--soft); }
.pbf-plan ul { margin:12px 0 0; padding-left:18px; font-size:14px; font-weight:700; color:var(--soft); line-height:1.8; }
.pbf-faq { max-width:780px; margin:0 auto; }
.pbf-q { background:#fff; border:1px solid var(--line); border-radius:16px; margin-bottom:10px; overflow:hidden; }
.pbf-q button { width:100%; text-align:left; background:none; border:none; cursor:pointer; padding:17px 20px; font-size:16px; font-weight:900; color:var(--ink); font-family:inherit; display:flex; justify-content:space-between; gap:12px; }
.pbf-q p { margin:0; padding:0 20px 18px; font-size:15px; color:var(--soft); font-weight:700; line-height:1.7; }
.pbf-final { text-align:center; background:linear-gradient(135deg,#FFE4EC,#F3EEFF); }
.pbf-tabs { position:sticky; top:58px; z-index:19; background:rgba(252,252,255,.95); backdrop-filter:blur(10px); border-bottom:1px solid var(--line); }
.pbf-tabs .pbf-wrap { display:flex; gap:6px; overflow-x:auto; scrollbar-width:none; padding-top:8px; padding-bottom:8px; }
.pbf-tabs .pbf-wrap::-webkit-scrollbar { display:none; }
.pbf-tab { flex:none; text-decoration:none; font-size:14px; font-weight:800; color:var(--soft); padding:8px 14px; border-radius:99px; white-space:nowrap; transition:background .2s,color .2s; }
.pbf-tab:hover { background:#FFF0F4; color:var(--pink); }
.pbf-tab.on { background:var(--pink); color:#fff; }
.pbf-more { display:inline-block; margin-top:6px; font-weight:900; color:var(--pink); text-decoration:none; }
.pbf-tsteps { display:grid; grid-template-columns:repeat(auto-fit,minmax(240px,1fr)); gap:20px; }
.pbf-next { display:flex; flex-wrap:wrap; gap:10px; justify-content:center; }
.pbf-foot { text-align:center; padding:26px 16px 40px; font-size:13px; color:var(--soft); font-weight:700; }
.pbf-foot a { margin:0 8px; }
`;

function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="pbf-q">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        <span>{q}</span><span style={{ color: "var(--pink)" }}>{open ? "−" : "+"}</span>
      </button>
      {open && <p>{a}</p>}
    </div>
  );
}

function Shots({ f }) {
  if (f.phones) {
    return (
      <div className="pbf-shots2" style={{ maxWidth: 520, margin: "0 auto" }}>
        {f.shots.map(s => <div key={s} className="pbf-phone"><img src={IMG(s)} alt="" loading="lazy" /></div>)}
      </div>
    );
  }
  if (f.phoneSecond) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 14, alignItems: "center" }}>
        <img className="pbf-shot" src={IMG(f.shots[0])} alt="" loading="lazy" />
        <div className="pbf-phone"><img src={IMG(f.shots[1])} alt="" loading="lazy" /></div>
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: f.shots.length > 1 ? "1.7fr 1fr" : "1fr", gap: 14, alignItems: "start", maxWidth: f.shots.length > 1 ? "none" : 460, margin: "0 auto" }}>
      {f.shots.map(s => <img key={s} className="pbf-shot" src={IMG(s)} alt="" loading="lazy" />)}
    </div>
  );
}

// Break a headline after its first comma (， or ,), keeping the comma.
// No regex lookbehind: Safari before 16.4 can't parse it, which would take
// the whole page down on older iPhones.
const headline = (text) => {
  const m = String(text).match(/^(.*?[，,])\s*(.+)$/);
  return m ? <>{m[1]}<br />{m[2]}</> : text;
};

// Language comes from the path: /features… is Chinese, /en/features… English.
// /en/features is always English. Bare /features follows the visitor's app
// language (en/ja → English) except for crawlers, which keep the zh page.
const langFromPath = () => {
  try {
    if (/^\/en\/features/.test(window.location.pathname)) return "en";
    if (/bot|crawl|spider|slurp|lighthouse/i.test(navigator.userAgent || "")) return "zh";
    return i18nGetLocale().startsWith("zh") ? "zh" : "en";
  } catch { return "zh"; }
};
const taskFromPath = (C) => {
  try {
    const m = window.location.pathname.match(/^(?:\/en)?\/features\/([a-z-]+)\/?$/);
    return m && C.TASK_PAGES.some(t => t.slug === m[1]) ? m[1] : "";
  } catch { return ""; }
};

// Tab bar shared by the overview and every task page. Plain links (so they
// work without JS and crawlers follow them); clicks switch in place.
function Tabs({ C, task, go }) {
  const items = [["", C.UI.overviewTab], ...C.TASK_PAGES.map(t => [t.slug, t.tab])];
  // On a phone the bar scrolls sideways; keep the selected tab in view.
  useEffect(() => {
    try { document.querySelector(".pbf-tab.on")?.scrollIntoView({ inline: "center", block: "nearest" }); } catch { /* ignore */ }
  }, [task]);
  return (
    <div className="pbf-tabs">
      <div className="pbf-wrap" role="tablist">
        {items.map(([slug, label]) => (
          <a key={slug || "all"} role="tab" aria-selected={task === slug} className={"pbf-tab" + (task === slug ? " on" : "")}
            href={slug ? C.base + "/" + slug : C.base} onClick={(e) => { e.preventDefault(); go(slug); }}>{label}</a>
        ))}
      </div>
    </div>
  );
}

function CaseStudy({ C, withText }) {
  return (
    <div className="pbf-case">
      <p className="pbf-kick">{C.UI.realShop}</p>
      <h2 style={{ fontSize: "clamp(24px,3.4vw,34px)", fontWeight: 900, margin: 0, lineHeight: 1.3 }}>{C.CASE.title}</h2>
      <div className="pbf-stats">
        {C.CASE.stats.map(([n, l]) => <div key={l} className="pbf-stat"><b>{n}</b><span>{l}</span></div>)}
      </div>
      {withText && <p style={{ margin: 0, fontSize: 16, fontWeight: 700, opacity: .9, lineHeight: 1.7 }}>{C.CASE.text}</p>}
    </div>
  );
}

function TaskPage({ C, t, go, signup }) {
  const U = C.UI;
  const steps = taskSteps(t, C.BOOKING_STEPS);
  const faq = taskFaq(t, C.FAQ);
  const others = C.TASK_PAGES.filter(o => o.slug !== t.slug);
  return (
    <>
      <header className="pbf-hero">
        <div className="pbf-wrap">
          <div>
            <p className="pbf-kick">{t.kicker}</p>
            <h1 className="pbf-h1">{headline(t.title)}</h1>
            <p className="pbf-lead">{t.lead}</p>
            <div style={{ marginBottom: 18 }}>{t.badges.map(([b, free]) => <span key={b} className={"pbf-badge" + (free ? " free" : "")}>{b}</span>)}</div>
            <div className="pbf-ctas">
              <button className="pbf-btn pri" onClick={signup}>{U.signup}</button>
              <a className="pbf-btn sec" href="/shop/demo-grooming" target="_blank" rel="noopener">{U.demo}</a>
            </div>
          </div>
          {t.video
            ? <video className="pbf-video" src={IMG(t.video[0])} poster={IMG(t.video[1])} controls playsInline muted preload="metadata" aria-label={t.title} />
            : <Shots f={t.shots} />}
        </div>
      </header>

      <section className="pbf-sec">
        <div className="pbf-wrap">
          <h2 className="pbf-h2">{t.stepsTitle || U.howItWorks}</h2>
          <p className="pbf-sub">{U.realScreens}</p>
          {t.stepsPhones ? (
            <div className="pbf-steps">
              {steps.map(([img, st, d]) => (
                <div key={st} className="pbf-step">
                  <div className="pbf-phone"><img src={IMG(img)} alt={st} loading="lazy" /></div>
                  <b>{st}</b><span>{d}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="pbf-tsteps">
              {steps.map(([img, st, d]) => (
                <div key={st} className="pbf-card">
                  <img src={IMG(img)} alt={st} loading="lazy" />
                  <div><h4>{st}</h4><p>{d}</p></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {t.aiDemo && (
        <section className="pbf-sec alt">
          <div className="pbf-wrap">
            <h2 className="pbf-h2">{t.aiDemo.title}</h2>
            <p className="pbf-sub">{t.aiDemo.sub}</p>
            <video className="pbf-video" src={IMG(t.aiDemo.video[0])} poster={IMG(t.aiDemo.video[1])} controls playsInline muted preload="none"
              aria-label={t.aiDemo.title} style={{ maxWidth: 960, margin: "0 auto" }} />
          </div>
        </section>
      )}

      <section className={"pbf-sec" + (t.aiDemo ? "" : " alt")}>
        <div className="pbf-wrap" style={{ maxWidth: 760 }}>
          <div className="pbf-feat" style={{ gridTemplateColumns: "1fr", marginBottom: 0 }}>
            <div>
              <h3 style={{ textAlign: "center" }}>{U.keyPoints}</h3>
              <ul>{t.points.map(p => <li key={p}>{p}</li>)}</ul>
            </div>
          </div>
        </div>
      </section>

      {t.proof && (
        <section className="pbf-sec">
          <div className="pbf-wrap"><CaseStudy C={C} /></div>
        </section>
      )}

      {faq.length > 0 && (
        <section className={"pbf-sec" + (t.proof ? " alt" : "")}>
          <div className="pbf-wrap">
            <h2 className="pbf-h2">{U.faqTitle}</h2>
            <div className="pbf-faq" style={{ marginTop: 28 }}>
              {faq.map(([q, a]) => <Faq key={q} q={q} a={a} />)}
            </div>
          </div>
        </section>
      )}

      <section className="pbf-sec pbf-final">
        <div className="pbf-wrap">
          <h2 className="pbf-h2">{U.taskFinalTitle}</h2>
          <p className="pbf-sub">{U.taskFinalSub}</p>
          <div className="pbf-next" style={{ marginBottom: 26 }}>
            {others.map(o => (
              <a key={o.slug} className="pbf-btn sec sm" href={C.base + "/" + o.slug} onClick={(e) => { e.preventDefault(); go(o.slug); }}>{o.tab} →</a>
            ))}
          </div>
          <div className="pbf-ctas" style={{ justifyContent: "center" }}>
            <button className="pbf-btn pri" onClick={signup}>{U.signup}</button>
            <a className="pbf-btn sec" href={C.base + "#pricing"} onClick={(e) => { e.preventDefault(); go("", "pricing"); }}>{U.seePricing}</a>
          </div>
        </div>
      </section>
    </>
  );
}

function Overview({ C, go, signup }) {
  const U = C.UI;
  return (
    <>
      <header className="pbf-hero">
        <div className="pbf-wrap">
          <div>
            <p className="pbf-kick">{C.HERO.kicker}</p>
            <h1 className="pbf-h1">{headline(C.HERO.title)}</h1>
            <p className="pbf-lead">{C.HERO.lead}</p>
            <div className="pbf-ctas">
              <button className="pbf-btn pri" onClick={signup}>{U.signup}</button>
              <a className="pbf-btn sec" href="/shop/demo-grooming" target="_blank" rel="noopener">{U.demo}</a>
            </div>
            <p className="pbf-note">{U.heroNote}</p>
            <a className="pbf-play" href={PLAY_STORE_URL} target="_blank" rel="noopener">
              <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 3.2v17.6c0 .5.5.8.9.6L20 12 4.9 2.6c-.4-.2-.9.1-.9.6z" fill="#34D399"/><path d="M4 3.2 14.2 12 4 20.8z" fill="#60A5FA" opacity=".9"/></svg>
              <span><small>{U.playApp}</small><b>{U.playGet}</b></span>
            </a>
          </div>
          <video className="pbf-video" src={IMG(U.heroVideo[0])} poster={IMG(U.heroVideo[1])} controls playsInline muted preload="metadata" />
        </div>
      </header>

      <section className="pbf-sec">
        <div className="pbf-wrap">
          <h2 className="pbf-h2">{U.baTitle}</h2>
          <p className="pbf-sub">{U.baSub}</p>
          <div className="pbf-ba">
            {[[C.BA.before, "#F6F5F9"], [C.BA.after, "#FFEFF4"]].map(([[h, items], bg]) => (
              <div key={h} style={{ background: bg }}>
                <h3>{h}</h3>
                <ul>{items.map(x => <li key={x}>{x}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pbf-sec alt" id="why">
        <div className="pbf-wrap">
          <h2 className="pbf-h2">{U.whyTitle}</h2>
          <p className="pbf-sub">{U.realScreens}</p>
          {C.FEATURES.map((f, i) => (
            <div key={f.title} className={"pbf-feat" + (i % 2 ? " flip" : "")}>
              <div>
                <span className="pbf-num">{i + 1}</span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
                <ul>{f.points.map(p => <li key={p}>{p}</li>)}</ul>
                {f.badges.map(([b, free]) => <span key={b} className={"pbf-badge" + (free ? " free" : "")}>{b}</span>)}
                {f.more && <div><a className="pbf-more" href={C.base + "/" + f.more} onClick={(e) => { e.preventDefault(); go(f.more); }}>{U.more}</a></div>}
              </div>
              {f.video
                ? <video className="pbf-video" src={IMG(f.video[0])} poster={IMG(f.video[1])} controls playsInline muted preload="none" aria-label={f.title} />
                : <Shots f={f} />}
            </div>
          ))}
        </div>
      </section>

      <section className="pbf-sec" id="booking">
        <div className="pbf-wrap">
          <h2 className="pbf-h2">{U.bookingTitle}</h2>
          <p className="pbf-sub">{U.bookingSub} <a href="/shop/demo-grooming" target="_blank" rel="noopener" style={{ color: "var(--pink)" }}>{U.openDemo}</a></p>
          <div className="pbf-steps">
            {C.BOOKING_STEPS.map(([img, t, d]) => (
              <div key={t} className="pbf-step">
                <div className="pbf-phone"><img src={IMG(img)} alt={t} loading="lazy" /></div>
                <b>{t}</b><span>{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="pbf-sec alt" id="day">
        <div className="pbf-wrap">
          <h2 className="pbf-h2">{U.dayTitle}</h2>
          <p className="pbf-sub">{U.daySub}</p>
          {U.dayVideo[0] !== U.heroVideo[0] && (
            <video className="pbf-video" src={IMG(U.dayVideo[0])} poster={IMG(U.dayVideo[1])} controls playsInline muted preload="none"
              aria-label={U.dayVideo[2]} style={{ maxWidth: 880, margin: "28px auto 32px" }} />
          )}
          <div className="pbf-day">
            {C.DAY.map(([img, k, t, d]) => (
              <div key={t} className="pbf-card">
                <img src={IMG(img)} alt={t} loading="lazy" />
                <div><small>{k}</small><h4>{t}</h4><p>{d}</p></div>
              </div>
            ))}
          </div>
          <p style={{ textAlign: "center", marginTop: 26, fontWeight: 800, color: "var(--soft)" }}>
            {U.helpLine}<a href="/help" style={{ color: "var(--pink)" }}>{U.helpLink}</a>
          </p>
        </div>
      </section>

      <section className="pbf-sec">
        <div className="pbf-wrap"><CaseStudy C={C} withText /></div>
      </section>

      <section className="pbf-sec alt" id="pricing">
        <div className="pbf-wrap">
          <h2 className="pbf-h2">{U.pricingTitle}</h2>
          <p className="pbf-sub">{U.pricingSub}</p>
          <div className="pbf-plans">
            {C.PLANS.map(([n, p, items, hl]) => (
              <div key={n} className={"pbf-plan" + (hl ? " hl" : "")}>
                <h4>{n}{hl && <span className="pbf-badge" style={{ marginLeft: 8 }}>{U.recommended}</span>}</h4>
                <div className="pr">HK${p}<small>{U.perMonth}</small></div>
                <ul>{items.map(x => <li key={x}>{x}</li>)}</ul>
              </div>
            ))}
          </div>
          <p className="pbf-sub" style={{ marginTop: 18, fontSize: 14 }}>{U.aiNote}</p>
        </div>
      </section>

      <section className="pbf-sec">
        <div className="pbf-wrap">
          <h2 className="pbf-h2">{U.faqTitle}</h2>
          <div className="pbf-faq" style={{ marginTop: 28 }}>
            {C.FAQ.map(([q, a]) => <Faq key={q} q={q} a={a} />)}
          </div>
        </div>
      </section>

      <section className="pbf-sec pbf-final">
        <div className="pbf-wrap">
          <h2 className="pbf-h2">{U.finalTitle}</h2>
          <p className="pbf-sub">{U.finalSub}</p>
          <div className="pbf-ctas" style={{ justifyContent: "center" }}>
            <button className="pbf-btn pri" onClick={signup}>{U.signup}</button>
            <a className="pbf-btn sec" href="/shop/demo-grooming" target="_blank" rel="noopener">{U.demo}</a>
          </div>
        </div>
      </section>
    </>
  );
}

export default function FeaturesPage({ onSignup, onHome }) {
  const signup = (e) => { e?.preventDefault?.(); onSignup?.(); };
  const [lang, setLang] = useState(langFromPath);
  const C = lang === "en" ? EN : ZH;
  const U = C.UI;
  const [task, setTask] = useState(() => taskFromPath(lang === "en" ? EN : ZH));
  // Back/forward between tabs (and languages).
  useEffect(() => {
    const onPop = () => { const l = langFromPath(); setLang(l); setTask(taskFromPath(l === "en" ? EN : ZH)); };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const current = C.TASK_PAGES.find(t => t.slug === task) || null;
  useEffect(() => {
    try {
      document.title = current ? current.seo.title : U.overviewTitle;
      document.documentElement.lang = U.lang;
    } catch { /* ignore */ }
  }, [current, U]);
  const go = (slug, anchor, toLang) => {
    const L = toLang || lang;
    const base = L === "en" ? EN.base : ZH.base;
    const path = slug ? base + "/" + slug : base;
    try { if (window.location.pathname !== path) window.history.pushState({}, "", path + (anchor ? "#" + anchor : "")); } catch { /* ignore */ }
    if (L !== lang) setLang(L);
    setTask(slug);
    setTimeout(() => {
      const el = anchor && document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth" }); else window.scrollTo({ top: 0, behavior: "smooth" });
    }, 30);
  };
  const otherLang = lang === "en" ? "zh" : "en";
  return (
    <div className="pbf" lang={U.lang}>
      <style>{FEATURES_CSS}</style>

      <nav className="pbf-nav">
        <div className="pbf-wrap">
          <a className="pbf-logo" href="/" onClick={(e) => { if (onHome) { e.preventDefault(); onHome(); } }}>🐾 PettoBook</a>
          <div className="pbf-navlinks">
            <a href={lang === "en" ? "/en/pricing" : "/pricing"}>{U.navPricing}</a>
            <a href={lang === "en" ? "/en/help" : "/help"}>{U.navHelp}</a>
            <a href="/directory">{U.navDirectory}</a>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <a className="pbf-tab" style={{ border: "1px solid var(--line)" }} hrefLang={otherLang === "en" ? "en" : "zh-HK"}
              href={(otherLang === "en" ? EN.base : ZH.base) + (task ? "/" + task : "")}
              onClick={(e) => { e.preventDefault(); go(task, undefined, otherLang); }}>{U.switchTo[1]}</a>
            <button className="pbf-btn pri sm" onClick={signup}>{U.signup}</button>
          </div>
        </div>
      </nav>
      <Tabs C={C} task={task} go={go} />

      {current ? <TaskPage C={C} t={current} go={go} signup={signup} /> : <Overview C={C} go={go} signup={signup} />}

      <footer className="pbf-foot">
        {U.foot.map(([href, label], i) => <span key={href}>{i > 0 && "·"}<a href={href}>{label}</a></span>)}
        <div style={{ marginTop: 10 }}>© PettoBook</div>
      </footer>
    </div>
  );
}
